import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useOrdersEnabled } from "@/hooks/useOrdersEnabled";

export default function ProductDetail() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [active, setActive] = useState(0);
  const [qty, setQty] = useState(1);
  const { addItem, isWholesale, minQty } = useCart();
  const ordersEnabled = useOrdersEnabled();

  useEffect(() => {
    setData(null);
    api.get(`/products/${slug}`).then(r => { setData(r.data); setActive(0); }).catch(() => {});
  }, [slug]);

  const isPrint = data?.product?.product_type === "print";

  // Every visit starts the quantity at 0 — nothing is remembered on remount.
  useEffect(() => { setQty(0); }, [slug]);

  if (!data) return <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-24 text-brand-neutral">Loading…</div>;
  const { product, related } = data;

  const detailRows = isPrint
    ? [
        { label: "Size", value: product.size },
        { label: "Medium", value: "Premium archival-quality photographic paper" },
        { label: "Edition", value: product.edition },
        { label: "Item Number", value: product.sku },
      ].filter(r => r.value)
    : [
        { label: "Dimensions", value: product.dimensions },
        { label: "Materials", value: product.materials },
        { label: "Occasion", value: product.occasion },
        { label: "Item Number", value: product.sku },
      ].filter(r => r.value);

  const backTo = isPrint ? "/prints" : "/products";
  const backLabel = isPrint ? "Back to prints" : "Back to catalogue";

  return (
    <div data-testid="product-detail-page" className="fade-up">
      <section className="max-w-[1440px] mx-auto px-6 md:px-12 pt-10">
        <Link to={backTo} data-testid="back-to-products" className="inline-flex items-center gap-2 text-sm uppercase tracking-widest text-brand-neutral hover:text-brand-accent">
          <ArrowLeft strokeWidth={1.5} className="w-4 h-4" /> {backLabel}
        </Link>
      </section>

      <section className="max-w-[1200px] mx-auto px-6 md:px-12 py-12 md:py-16">
        <div className="grid md:grid-cols-12 gap-12 items-start">
          <div className="md:col-span-6">
            <div className="max-w-[380px] md:max-w-[420px] mx-auto">
              <div className="aspect-[5/7] overflow-hidden bg-brand-surface">
                <img data-testid="product-main-image" src={product.images?.[active]} alt={product.title} className="w-full h-full object-cover" />
              </div>
              {product.images?.length > 1 && (
                <div className="grid grid-cols-4 gap-3 mt-4">
                  {product.images.map((im, i) => (
                    <button
                      key={i}
                      data-testid={`product-thumb-${i}`}
                      onClick={() => setActive(i)}
                      className={`aspect-square overflow-hidden ${i === active ? "ring-1 ring-brand-accent" : "opacity-70 hover:opacity-100"}`}
                    >
                      <img src={im} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="md:col-span-6 md:pl-4">
            <p className="overline hidden">{product.collection}</p>
            <h1 data-testid="product-title" className="font-serif font-light text-3xl md:text-4xl text-brand-dark mt-3 leading-tight">
              {product.title}
            </h1>
            {isWholesale && product.wholesale_price != null ? (
              <div className="mt-4">
                <p className="text-2xl text-brand-accent font-medium">Wholesale: ${product.wholesale_price.toFixed(2)}</p>
                <p className="text-sm text-brand-neutral mt-1">MSRP: ${product.price?.toFixed(2)}</p>
              </div>
            ) : (
              <p className="mt-4 text-2xl text-brand-neutral font-light">${product.price?.toFixed(2)}</p>
            )}
            <p className="mt-6 text-brand-neutral leading-relaxed">{product.description}</p>

            {detailRows.length > 0 && (
              <dl data-testid="product-details" className="mt-8 border-t border-brand-border">
                {detailRows.map(({ label, value }) => (
                  <div key={label} className="flex justify-between items-baseline py-3 border-b border-brand-border gap-6">
                    <dt className="text-[11px] tracking-[0.16em] uppercase text-brand-neutral">{label}</dt>
                    <dd className="text-brand-dark text-right text-sm whitespace-pre-line">{value}</dd>
                  </div>
                ))}
              </dl>
            )}

            {isPrint && (
              <div data-testid="custom-print-cta" className="mt-8 p-6 border border-brand-border bg-brand-surface paper-texture">
                <p className="overline text-brand-accent">Looking for a different size?</p>
                <p className="mt-3 text-brand-neutral leading-relaxed text-sm">
                  Many of our illustrations can be produced in custom sizes. If you're looking for
                  something larger or have a specific space in mind, we'd be happy to help.
                </p>
                <Link
                  to={`/quote?service=fine_art_prints&print=${encodeURIComponent(product.title)}`}
                  data-testid="custom-print-inquire"
                  className="mt-4 inline-flex items-center gap-1.5 text-[11px] tracking-[0.16em] uppercase text-brand-accent hover:text-brand-dark border-b border-brand-accent hover:border-brand-dark pb-0.5"
                >
                  Inquire About a Custom Print →
                </Link>
              </div>
            )}

            {product.sold_out ? (
              <div
                data-testid="detail-sold-out"
                className="mt-10 border-y border-brand-border py-6 text-center"
              >
                <p className="text-[11px] tracking-[0.24em] uppercase text-brand-neutral">
                  Sold Out
                </p>
                <p className="mt-3 font-serif italic text-brand-dark text-lg">
                  This edition is complete.
                </p>
                <p className="mt-3 text-sm text-brand-neutral max-w-md mx-auto leading-relaxed">
                  All {product.edition_size} prints in this edition have found their homes.
                  {" "}<Link
                    to={`/quote?service=fine_art_prints&print=${encodeURIComponent(product.title)}`}
                    className="underline underline-offset-4 text-brand-accent"
                  >
                    Inquire about a commission
                  </Link>
                  {" "}for something similar.
                </p>
              </div>
            ) : ordersEnabled !== true ? (
              <div
                data-testid="orders-disabled-detail"
                className="mt-10 border border-brand-border bg-[#F4F2EC] px-6 py-6 text-center"
              >
                <p className="text-[11px] tracking-[0.28em] uppercase text-brand-accent font-medium">
                  Order request options coming soon
                </p>
                <p className="mt-3 text-sm text-brand-neutral leading-relaxed max-w-md mx-auto">
                  The full ordering experience is being refined and will
                  return shortly. Please check back soon.
                </p>
              </div>
            ) : (
              <>
                {isPrint && Number.isFinite(product.remaining) && Number.isFinite(product.edition_size)
                  && product.remaining < product.edition_size && (
                    <p
                      data-testid="detail-remaining"
                      className="mt-8 text-[11px] tracking-[0.24em] uppercase text-brand-accent"
                    >
                      {product.remaining} of {product.edition_size} remaining
                    </p>
                  )}
                <div className="mt-10 flex items-center gap-4">
                <div className="flex items-center border border-brand-border">
                  <button data-testid="qty-minus" onClick={() => setQty(Math.max(0, qty - 1))} className="px-4 py-3 text-brand-neutral hover:text-brand-accent">−</button>
                  <span data-testid="qty-value" className="px-5 py-3 text-brand-dark min-w-[3rem] text-center">{qty}</span>
                  <button data-testid="qty-plus" onClick={() => setQty(qty + 1)} className="px-4 py-3 text-brand-neutral hover:text-brand-accent">+</button>
                </div>
                <button
                  data-testid="add-to-order-btn"
                  disabled={qty === 0 || (!isPrint && isWholesale && qty < minQty)}
                  onClick={() => {
                    if (qty === 0) return;
                    if (!isPrint && isWholesale && qty < minQty) { toast.error(`Wholesale minimum is ${minQty} per design.`); return; }
                    addItem(product, qty);
                    toast.success(`${qty} × ${product.title} added to your order.`);
                    setQty(0);
                  }}
                  className="btn-primary flex-1 justify-center disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ShoppingBag strokeWidth={1.5} className="w-4 h-4" /> Add to Order
                </button>
              </div>
              </>
            )}
            {!isPrint && isWholesale && !product.sold_out && (
              <p data-testid="product-min-qty-hint" className="mt-3 text-xs text-brand-neutral">
                Wholesale minimum: {minQty} cards per design.
              </p>
            )}
          </div>
        </div>
      </section>

      {related?.length > 0 && (
        <section className="bg-brand-surface py-24">
          <div className="max-w-[1440px] mx-auto px-6 md:px-12">
            <p className="overline hidden">From the same collection</p>
            <h2 className="font-serif text-3xl md:text-4xl text-brand-dark mt-3">You may also love</h2>
            <div className="grid md:grid-cols-4 gap-8 mt-12">
              {related.map((p) => (
                <Link key={p.slug} to={`/products/${p.slug}`} data-testid={`related-${p.slug}`} className="group">
                  <div className="img-zoom aspect-[5/7] overflow-hidden">
                    <img src={p.images?.[0]} alt={p.title} className="w-full h-full object-cover" />
                  </div>
                  <h3 className="font-serif text-xl mt-4 text-brand-dark group-hover:text-brand-accent transition-colors">{p.title}</h3>
                  <p className="text-brand-neutral text-sm mt-1">${p.price?.toFixed(2)}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
